import { useEffect, useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import LoadingWheel from "../shared/LoadingWheel";
import SerpQueryPreview from "./SerpQueryPreview";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import useSerpQuerySettings from "../../data/SerpQuery/useSerpQuerySettings";
import useSerpQuerySettingsMutations from "../../data/SerpQuery/useSerpQuerySettingsMutations";
import {
  fieldFor,
  ISerpQueryParameter,
  ISerpQueryProfileSettings,
  sellersToLines,
  SerpQueryProfile,
  serpQueryProfiles,
} from "../../models/SerpQuery/SerpQuery";

interface DraftParameter extends ISerpQueryParameter {
  clientId: string;
}

let nextClientId = 0;

function draftId() {
  nextClientId += 1;
  return `serp-${nextClientId}`;
}

function toDraft(parameters: ISerpQueryParameter[]): DraftParameter[] {
  return parameters.map((parameter) => {
    const field = fieldFor(parameter.name);
    return {
      ...parameter,
      value: field?.sellers ? sellersToLines(parameter.value) : parameter.value,
      clientId: draftId(),
    };
  });
}

function toPayload(parameters: DraftParameter[]): ISerpQueryParameter[] {
  return parameters.map((parameter, index) => ({
    name: parameter.name.trim(),
    value: parameter.value,
    sortOrder: index,
  }));
}

export default function SerpSettingsModal({ open, setOpen }: ModalControlProps) {
  const settings = useSerpQuerySettings(open);
  const { save } = useSerpQuerySettingsMutations();
  const [profile, setProfile] = useState<SerpQueryProfile>(SerpQueryProfile.EbaySearch);
  const [drafts, setDrafts] = useState<Record<number, DraftParameter[]>>({});

  useEffect(() => {
    if (!open || !settings.data) return;

    const next: Record<number, DraftParameter[]> = {};
    settings.data.profiles.forEach((item) => {
      next[item.profile] = toDraft(item.parameters);
    });
    setDrafts(next);
  }, [open, settings.data]);

  const current = serpQueryProfiles.find((item) => item.profile === profile)!;
  const rows = drafts[profile] ?? [];
  const saved = settings.data?.profiles.find((item) => item.profile === profile);
  const usingDefaults = saved?.usingDefaults;

  const patchRow = (clientId: string, patch: Partial<DraftParameter>) => {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [profile]: (currentDrafts[profile] ?? []).map((row) =>
        row.clientId === clientId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const removeRow = (clientId: string) => {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [profile]: (currentDrafts[profile] ?? []).filter((row) => row.clientId !== clientId),
    }));
  };

  const addRow = () => {
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [profile]: [
        ...(currentDrafts[profile] ?? []),
        { clientId: draftId(), name: "", value: "", sortOrder: 0 },
      ],
    }));
  };

  const handleSave = () => {
    if (!settings.data) return;

    const profiles: ISerpQueryProfileSettings[] = serpQueryProfiles.map((item) => ({
      profile: item.profile,
      usingDefaults: false,
      parameters: toPayload(drafts[item.profile] ?? []),
    }));

    save.mutate(
      { profiles },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Serp settings</h2>
          <p className="mt-1 text-sm text-gray-500">
            The query string sent to SerpApi. Saved values are used on the next lookup.
            Price estimates keep a result for 30 days; changing the active listing query
            starts a fresh search the next time that term is priced.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {serpQueryProfiles.map((item) => (
            <button
              key={item.profile}
              type="button"
              onClick={() => setProfile(item.profile)}
              className={
                item.profile === profile
                  ? "rounded-full bg-orange-600 px-3 py-1 text-sm font-medium text-white"
                  : "rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200"
              }
            >
              {item.title}
            </button>
          ))}
        </div>

        <div>
          <p className="text-sm text-gray-700">{current.description}</p>
          <p className="mt-1 text-xs text-gray-500">{current.reserved}</p>
          {usingDefaults && (
            <p className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              These are the built-in values. Saving stores your copy.
            </p>
          )}
        </div>

        {saved?.queryTemplate && (
          <SerpQueryPreview
            title="Query as saved"
            query={saved.queryTemplate}
            hint="Highlighted tags are filled in per search. Replace them before running the query by hand; the playground adds your own API key."
          />
        )}

        {settings.isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingWheel />
          </div>
        ) : settings.isError ? (
          <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {(settings.error as Error)?.message || "Could not load Serp settings."}
          </p>
        ) : (
          <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
            {rows.map((row) => {
              const field = fieldFor(row.name);
              const known = !!field && row.name.trim().length > 0;

              return (
                <div key={row.clientId} className="rounded border border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      {known ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">{field.label}</p>
                          <p className="font-mono text-xs text-gray-500">{row.name}</p>
                        </div>
                      ) : (
                        <TextField
                          label="Parameter"
                          value={row.name}
                          fullWidth
                          onChange={(event) =>
                            patchRow(row.clientId, {
                              name: (event.target as HTMLInputElement).value,
                            })
                          }
                        />
                      )}

                      {field?.sellers ? (
                        <label className="flex flex-col space-y-1">
                          <span className="text-sm font-medium text-black opacity-80">
                            Usernames
                          </span>
                          <textarea
                            value={row.value}
                            rows={8}
                            onChange={(event) =>
                              patchRow(row.clientId, { value: event.target.value })
                            }
                            className="w-full rounded border border-orange-600 px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-orange-600"
                          />
                        </label>
                      ) : (
                        <TextField
                          label="Value"
                          value={row.value}
                          fullWidth
                          onChange={(event) =>
                            patchRow(row.clientId, {
                              value: (event.target as HTMLInputElement).value,
                            })
                          }
                        />
                      )}

                      {field && <p className="text-xs text-gray-500">{field.hint}</p>}
                    </div>

                    <button
                      type="button"
                      className="mt-1 rounded p-1 text-gray-500 hover:text-red-700"
                      title="Remove parameter"
                      onClick={() => removeRow(row.clientId)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })}

            <Button variant="outlined" size="sm" onClick={addRow}>
              <span className="inline-flex items-center gap-1">
                <PlusIcon className="h-4 w-4" />
                Add parameter
              </span>
            </Button>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outlined" onClick={() => setOpen(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={save.isPending || settings.isLoading || !settings.data}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
